import { Effect, Layer, Result, Scope } from "effect";
import { ReviewHistoryController, parseReviewDeepLink, reviewDeepLinkExitSearch, type ReviewWindowLike } from "@workerstation/shared/review-deep-link";
import { reviewNavigationFailure } from "./model.ts";
import { ReviewNavigationIo } from "./service.ts";

export const ReviewNavigationIoLive = Layer.succeed(ReviewNavigationIo, ReviewNavigationIo.of({
  open: (notify, canRead, report) => Effect.gen(function* () {
    const owner = yield* Scope.Scope;
    const alive = () => canRead() && owner.state._tag !== "Closed";
    if (!alive()) return yield* Effect.interrupt;
    const page = yield* Effect.try({ try: () => window, catch: reviewNavigationFailure });
    const listeners = new Map<() => void, () => void>();
    const remove = (listener: () => void) => {
      const receive = listeners.get(listener);
      if (!receive) return;
      const result = Result.try(() => page.removeEventListener("popstate", receive));
      if (Result.isFailure(result)) report(reviewNavigationFailure(result.failure));
      else listeners.delete(listener);
    };
    const port: ReviewWindowLike = {
      get location() { return page.location; },
      get history() { return page.history; },
      addEventListener: (_type, listener) => {
        const receive = listeners.get(listener) ?? (() => {
          if (!alive()) return;
          const result = Result.try(listener);
          if (Result.isFailure(result)) report(reviewNavigationFailure(result.failure));
        });
        listeners.set(listener, receive);
        page.addEventListener("popstate", receive);
      },
      removeEventListener: (_type, listener) => remove(listener)
    };
    const controller = new ReviewHistoryController(port, result => { if (alive()) notify(result); });
    yield* Effect.addFinalizer(() => Effect.try({ try: () => {
      controller.stop();
      for (const listener of listeners.keys()) remove(listener);
    }, catch: reviewNavigationFailure }).pipe(Effect.orDie));
    const perform = (operation: () => void) => Effect.try({ try: () => { if (alive()) operation(); }, catch: reviewNavigationFailure });
    return {
      receive: review => perform(() => {
        controller.start();
        if (review || parseReviewDeepLink(page.location.search).status !== "valid") return;
        const search = reviewDeepLinkExitSearch(page.location.search);
        page.history.replaceState(null, "", `${page.location.pathname}${search}${page.location.hash}`);
      }),
      navigate: (state, mode) => perform(() => { controller.start();controller.navigate(state, mode); })
    };
  })
}));
